import {
  fitPercentToRawScale,
  rawScaleToFitPercent,
  readStripFitScaleBaseline,
} from './fitScale'

export const TRANSFORM_KEYFRAME_PRESET_VERSION = 1
export const TRANSFORM_KEYFRAME_USER_PRESETS_KEY = 'vyby/transform/keyframePresets/v1'
export const TRANSFORM_KEYFRAME_PRESET_SOURCE_USER = 'user'
export const TRANSFORM_KEYFRAME_PRESET_SOURCE_GLOBAL = 'global'

export const TRANSFORM_KEYFRAME_GROUPS = {
  position: {
    id: 'position',
    name: 'Position',
    props: ['position.x', 'position.y', 'position.z'],
  },
  scale: {
    id: 'scale',
    name: 'Zoom',
    props: ['scale.x', 'scale.y', 'scale.z'],
  },
  rotation: {
    id: 'rotation',
    name: 'Rotation',
    props: ['rotation.x', 'rotation.y', 'rotation.z'],
  },
  opacity: {
    id: 'opacity',
    name: 'Opacity',
    props: ['opacity'],
  },
  volume: {
    id: 'volume',
    name: 'Volume',
    props: ['volume'],
  },
}

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const finite = (value, fallback = 0) => {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

const roundNumber = (value, decimals = 4) => {
  const next = Number(value)
  if (!Number.isFinite(next)) return value
  const factor = 10 ** decimals
  const rounded = Math.round(next * factor) / factor
  return Math.abs(rounded) < 1e-9 ? 0 : rounded
}

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, next]) => {
    if (typeof next !== 'undefined') acc[key] = next
    return acc
  }, {})

export const normalizeTransformPresetGroup = (group) => {
  const key = `${group || ''}`.trim()
  return TRANSFORM_KEYFRAME_GROUPS[key] ? key : ''
}

export const transformPresetValue = (source, id) => `${source}:${id}`

export const parseTransformPresetValue = (value) => {
  const raw = `${value || ''}`.trim()
  const match = raw.match(/^(global|user):(.+)$/)
  if (!match) return { source: '', id: '' }
  return {
    source: match[1],
    id: match[2],
  }
}

export const transformPresetDisplayName = (group) =>
  TRANSFORM_KEYFRAME_GROUPS[group]?.name || group || 'Keyframes'

export const propValueToPresetValue = (prop, value, scaleBaseline = {}) => {
  if (prop.startsWith('scale.')) {
    const axis = prop.split('.')[1]
    return roundNumber(rawScaleToFitPercent(value, scaleBaseline[axis] || 1), 3)
  }
  if (prop.startsWith('rotation.')) {
    return roundNumber((finite(value, 0) * 180) / Math.PI, 3)
  }
  return roundNumber(value, 3)
}

export const presetValueToPropValue = (prop, value, scaleBaseline = {}) => {
  if (prop.startsWith('scale.')) {
    const axis = prop.split('.')[1]
    return fitPercentToRawScale(finite(value, 100), scaleBaseline[axis] || 1)
  }
  if (prop.startsWith('rotation.')) {
    return (finite(value, 0) * Math.PI) / 180
  }
  return finite(value, 0)
}

export const normalizeTransformKeyframePreset = (preset = {}) => {
  if (!preset || typeof preset !== 'object') return null
  const group = normalizeTransformPresetGroup(preset.group)
  const id = `${preset.id || preset.code || ''}`.trim()
  const name = `${preset.name || ''}`.trim()
  const keyframes = Array.isArray(preset.keyframes)
    ? preset.keyframes
        .map((kf) => {
          const props = kf?.props && typeof kf.props === 'object' ? kf.props : {}
          const nextProps = Object.entries(props).reduce((acc, [prop, value]) => {
            if (!TRANSFORM_KEYFRAME_GROUPS[group]?.props.includes(prop)) return acc
            acc[prop] = roundNumber(value, 3)
            return acc
          }, {})
          if (!Object.keys(nextProps).length) return null
          return compactObject({
            at: Math.max(0, Math.min(1, roundNumber(finite(kf?.at, 0), 5))),
            props: nextProps,
            easing: kf?.easing && typeof kf.easing === 'object' ? cloneJson(kf.easing) : undefined,
            interpOut:
              kf?.interpOut && typeof kf.interpOut === 'object'
                ? cloneJson(kf.interpOut)
                : undefined,
          })
        })
        .filter(Boolean)
    : []
  if (!group || !id || !name || !keyframes.length) return null

  return compactObject({
    ...preset,
    id,
    code: preset.code || id,
    name,
    group,
    version: Number.isFinite(Number(preset.version))
      ? Number(preset.version)
      : TRANSFORM_KEYFRAME_PRESET_VERSION,
    source: preset.source || TRANSFORM_KEYFRAME_PRESET_SOURCE_USER,
    type: 'transform-keyframe-preset',
    keyframes: keyframes.sort((a, b) => a.at - b.at),
    supportedProps: TRANSFORM_KEYFRAME_GROUPS[group].props,
    intentTags: Array.isArray(preset.intentTags) ? preset.intentTags.filter(Boolean) : undefined,
    description: typeof preset.description === 'string' ? preset.description : undefined,
    durationSec: Number.isFinite(Number(preset.durationSec))
      ? roundNumber(Number(preset.durationSec), 3)
      : undefined,
  })
}

export const createTransformKeyframeUserPreset = ({
  id,
  name,
  group,
  keyframes,
  durationSec,
  sourceRange = null,
  createdAt = Date.now(),
} = {}) =>
  normalizeTransformKeyframePreset({
    id,
    code: id,
    name,
    group,
    source: TRANSFORM_KEYFRAME_PRESET_SOURCE_USER,
    createdAt,
    durationSec,
    sourceRange,
    keyframes,
  })

export const buildTransformKeyframePresetFromStrip = ({
  strip,
  group,
  name,
  id,
  fps = 24,
} = {}) => {
  const normalizedGroup = normalizeTransformPresetGroup(group)
  const props = TRANSFORM_KEYFRAME_GROUPS[normalizedGroup]?.props || []
  const keyframes = Array.isArray(strip?.animation?.keyframes)
    ? strip.animation.keyframes
    : []
  if (!strip || !normalizedGroup || !props.length || !keyframes.length) return null

  const scaleBaseline = readStripFitScaleBaseline(strip, {
    x: strip?.animation?.values?.['scale.x'],
    y: strip?.animation?.values?.['scale.y'],
    z: strip?.animation?.values?.['scale.z'],
  })
  const selected = keyframes
    .map((kf) => {
      const nextProps = props.reduce((acc, prop) => {
        if (typeof kf?.props?.[prop] === 'undefined') return acc
        acc[prop] = propValueToPresetValue(prop, kf.props[prop], scaleBaseline)
        return acc
      }, {})
      if (!Object.keys(nextProps).length) return null
      return {
        time: Math.max(0, finite(kf.time, 0)),
        props: nextProps,
        easing: props.reduce((acc, prop) => {
          if (typeof kf?.easing?.[prop] !== 'undefined') acc[prop] = kf.easing[prop]
          return acc
        }, {}),
        interpOut: props.reduce((acc, prop) => {
          if (typeof kf?.interpOut?.[prop] !== 'undefined') acc[prop] = kf.interpOut[prop]
          return acc
        }, {}),
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time)
  if (!selected.length) return null

  const first = selected[0].time
  const last = selected[selected.length - 1].time
  const duration = Math.max(1 / Math.max(1, finite(fps, 24)), last - first)
  return createTransformKeyframeUserPreset({
    id,
    name,
    group: normalizedGroup,
    durationSec: duration,
    sourceRange: {
      startSec: roundNumber(first, 3),
      endSec: roundNumber(last, 3),
    },
    keyframes: selected.map((kf) =>
      compactObject({
        at: roundNumber((kf.time - first) / duration, 5),
        props: kf.props,
        easing: Object.keys(kf.easing || {}).length ? kf.easing : undefined,
        interpOut: Object.keys(kf.interpOut || {}).length ? kf.interpOut : undefined,
      })
    ),
  })
}

export const buildTransformKeyframePresetApplyFrames = ({
  preset,
  strip,
  fps = 24,
  range = null,
} = {}) => {
  const normalized = normalizeTransformKeyframePreset(preset)
  if (!normalized || !strip) return []
  const scaleBaseline = readStripFitScaleBaseline(strip, {
    x: strip?.animation?.values?.['scale.x'],
    y: strip?.animation?.values?.['scale.y'],
    z: strip?.animation?.values?.['scale.z'],
  })
  const clipFrameCount = Math.max(1, Math.round(finite(strip.length, 0) * Math.max(1, finite(fps, 24))))
  const startFrame = Math.max(0, Math.min(clipFrameCount - 1, Math.round(finite(range?.startFrame, 0))))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.min(clipFrameCount, Math.round(finite(range?.endFrameExclusive, clipFrameCount)))
  )
  const lastFrame = Math.max(startFrame, endFrameExclusive - 1)
  const frameSpan = Math.max(0, lastFrame - startFrame)

  return normalized.keyframes.map((kf) => {
    const frame = Math.max(
      0,
      Math.min(
        clipFrameCount - 1,
        Math.round(startFrame + Math.max(0, Math.min(1, finite(kf.at, 0))) * frameSpan)
      )
    )
    const props = Object.entries(kf.props || {}).reduce((acc, [prop, value]) => {
      acc[prop] = presetValueToPropValue(prop, value, scaleBaseline)
      return acc
    }, {})
    return compactObject({
      frame,
      props,
      easing: kf.easing,
      interpOut: kf.interpOut,
    })
  })
}

export const buildTransformPresetSummaries = (presets = {}) =>
  Object.values(presets || {})
    .map(normalizeTransformKeyframePreset)
    .filter(Boolean)
    .map((preset) =>
      compactObject({
        id: preset.id,
        name: preset.name,
        description: preset.description,
        intentTags: preset.intentTags,
        group: preset.group,
        supportedProps: preset.supportedProps,
        keyframeCount: preset.keyframes.length,
        durationSec: preset.durationSec,
      })
    )
